import React, { useState, useEffect, useCallback } from 'react';
import api, { getAccessToken } from '../api';
import ModelSelector from './ModelSelector';
import FileUpload from './FileUpload';
import MultiFileUpload from './MultiFileUpload';
import './InferenceForm.css';

const InferenceForm = () => {
  const [modelId, setModelId] = useState('');
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [inputSchema, setInputSchema] = useState(null);
  const [modelEndpoint, setModelEndpoint] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loras, setLoras] = useState([]);
  const [availableLoras, setAvailableLoras] = useState([]);
  const [error, setError] = useState(null);
  const [isContentPolicyViolation, setIsContentPolicyViolation] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [hasImageSize, setHasImageSize] = useState(false);
  const [imageSize, setImageSize] = useState('1024x768');
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [queueStatus, setQueueStatus] = useState(null);
  const [cancelUrl, setCancelUrl] = useState(null);
  const [eventSource, setEventSource] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputErrors, setInputErrors] = useState({});
  // Fields currently showing a free-text box instead of their preset select
  // (toggled via the "Custom…" option on select fields with allowCustom)
  const [customSelectFields, setCustomSelectFields] = useState({});
  // --- Voices array input state and handlers ---
  const [voices, setVoices] = useState([]);
  // Bumped whenever fetchModelSchema clears inputs, so upload fields remount
  // in the same render as the cleared data (not one render early, while
  // inputs still holds the previous model's file URLs).
  const [formResetKey, setFormResetKey] = useState(0);

  const normalizeAcceptToken = (token) => {
    if (!token) return null;

    const trimmed = String(token).trim().toLowerCase();
    if (!trimmed) return null;

    if (trimmed === 'image' || trimmed === 'images') return 'image/*';
    if (trimmed === 'audio' || trimmed === 'sounds' || trimmed === 'sound') return 'audio/*';
    if (trimmed === 'video' || trimmed === 'videos') return 'video/*';

    if (trimmed.includes('/')) {
      return trimmed;
    }

    if (trimmed.startsWith('.')) {
      return trimmed;
    }

    if (/^[a-z0-9]+$/.test(trimmed)) {
      return `.${trimmed}`;
    }

    return null;
  };

  const parseAcceptList = (value) => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .flatMap((item) => parseAcceptList(item))
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      return value
        .split(/[\s,;|]+/)
        .map(normalizeAcceptToken)
        .filter(Boolean);
    }

    return [];
  };

  const parseAcceptFromText = (text) => {
    if (!text || typeof text !== 'string') return [];

    const extensionMatches = text.match(/\b(?:png|jpe?g|webp|avif|heif|heic|gif|bmp|tiff|svg|mp3|wav|flac|ogg|m4a|aac|mp4|webm|mov|avi|mkv)\b/gi) || [];
    return extensionMatches
      .map((ext) => normalizeAcceptToken(ext))
      .filter(Boolean);
  };

  // Helper function to determine file accept types based on explicit schema constraints first.
  const getFileAcceptTypes = (fieldName, schema) => {
    const acceptCandidates = [
      schema?.accept,
      schema?.fileAccept,
      schema?.mimeTypes,
      schema?.mimetypes,
      schema?.allowedMimeTypes,
      schema?.contentTypes,
      schema?.allowedFileTypes,
      schema?.fileTypes,
      schema?.falConstraints?.accept,
      schema?.falConstraints?.mime_types,
      schema?.falConstraints?.mimeTypes,
      schema?.falConstraints?.content_types,
      schema?.falConstraints?.contentTypes,
      schema?.falConstraints?.allowed_file_types,
      schema?.falConstraints?.allowedFileTypes,
      schema?.items?.accept,
      schema?.items?.mimeTypes,
      schema?.items?.allowedMimeTypes,
      schema?.items?.contentTypes,
      schema?.items?.['x-fal']?.accept,
      schema?.items?.['x-fal']?.mime_types,
      schema?.items?.['x-fal']?.mimeTypes,
      schema?.items?.['x-fal']?.content_types,
      schema?.items?.['x-fal']?.contentTypes,
      schema?.items?.['x-fal']?.allowed_file_types,
      schema?.items?.['x-fal']?.allowedFileTypes
    ];

    const parsedFromSchema = acceptCandidates
      .flatMap((candidate) => parseAcceptList(candidate))
      .filter(Boolean);

    if (parsedFromSchema.length > 0) {
      return [...new Set(parsedFromSchema)].join(',');
    }

    const parsedFromText = [
      ...(parseAcceptFromText(schema?.description)),
      ...(parseAcceptFromText(schema?.limit_description)),
      ...(parseAcceptFromText(schema?.items?.description)),
      ...(parseAcceptFromText(schema?.items?.limit_description))
    ];

    if (parsedFromText.length > 0) {
      return [...new Set(parsedFromText)].join(',');
    }

    // Tokenize on non-alphanumeric boundaries so "video_url" doesn't get
    // mistaken for a bare "video" mention (and "image_urls" still matches
    // "image"). This matters because descriptions often reference *sibling*
    // fields by name (e.g. an `image_urls` field whose description says
    // "...optional when `video_url` is provided"), which a plain
    // substring/`.includes()` check would misread as this field being video.
    const tokenize = (text) => (text || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

    const CATEGORY_TOKENS = {
      video: ['video', 'videos'],
      audio: ['audio', 'audios', 'sound', 'sounds', 'music'],
      image: ['image', 'images', 'img', 'imgs', 'photo', 'photos', 'picture', 'pictures']
    };
    const CATEGORY_ACCEPT = { video: 'video/*', audio: 'audio/*', image: 'image/*' };
    const CATEGORY_ORDER = ['video', 'audio', 'image'];

    const matchCategory = (tokens) => {
      const tokenSet = new Set(tokens);
      return CATEGORY_ORDER.find((category) => CATEGORY_TOKENS[category].some((kw) => tokenSet.has(kw)));
    };

    // Field name and title are authoritative when they name the media type
    // directly — check them before ever looking at free-text descriptions.
    const nameTokens = [...tokenize(fieldName), ...tokenize(schema?.title)];
    const nameMatch = matchCategory(nameTokens);
    if (nameMatch) {
      return CATEGORY_ACCEPT[nameMatch];
    }

    const descriptionTokens = [
      ...tokenize(schema?.description),
      ...tokenize(schema?.items?.description)
    ];
    const descriptionMatch = matchCategory(descriptionTokens);
    if (descriptionMatch) {
      return CATEGORY_ACCEPT[descriptionMatch];
    }

    return '*/*';
  };

  const fetchModelSchema = useCallback(async () => {
    try {
      const response = await api.get(`/api/models/${modelId}`);
      const newSchema = response.data.input_schema;
      setInputSchema(newSchema);
      setModelEndpoint(response.data.api_endpoint);
      setSelectedModel(response.data);
      // Clear everything for the new model, except the prompt/text if the new
      // model has a matching field -- that's the one thing worth carrying over.
      setInputs(prev => {
        const preserved = {};
        ['prompt', 'text'].forEach((key) => {
          if (newSchema?.[key] !== undefined && prev[key] !== undefined) {
            preserved[key] = prev[key];
          }
        });
        return preserved;
      });
      setLoras([]);
      setVoices([]);
      setFormResetKey(k => k + 1);
    } catch (error) {
      console.error('Error fetching model schema:', error);
      setError('Failed to fetch model schema');
    }
  }, [modelId]);

  useEffect(() => {
    fetchAvailableLoras();
  }, []);

  useEffect(() => {
    if (modelId) {
      fetchModelSchema();
    }
  }, [modelId, fetchModelSchema]);

  useEffect(() => {
    if (inputSchema && inputSchema.image_size) {
      setHasImageSize(true);
      const sizeSchema = inputSchema.image_size;
      if (Array.isArray(sizeSchema.enum) && sizeSchema.enum.length > 0) {
        setImageSize(sizeSchema.default || sizeSchema.enum[0]);
      } else if (sizeSchema.default) {
        setImageSize(sizeSchema.default);
      }
    } else {
      setHasImageSize(false);
    }
  }, [inputSchema]);

  // Sync voices state with inputs when model/schema changes
  useEffect(() => {
    if (inputSchema && inputSchema.voices) {
      const min = inputSchema.voices.minItems || 1;
      setVoices((prev) => {
        // If already set, keep them, else initialize to min with default turn_prefix
        if (prev.length >= min) return prev;
        return Array.from({ length: min }, (_, i) => ({ voice: '', turn_prefix: `Spk${i + 1}:` }));
      });
    }
  }, [inputSchema]);

  const fetchAvailableLoras = async () => {
    try {
      const response = await api.get('/api/loras');
      setAvailableLoras(response.data);
    } catch (error) {
      console.error('Error fetching available LoRAs:', error);
      setError('Failed to fetch available LoRAs');
    }
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLoraChange = (index, loraId) => {
    const selectedLora = availableLoras.find(lora => lora.id === parseInt(loraId));
    
    const newLoras = [...loras];
    newLoras[index] = { path: selectedLora.path, scale: 1 };
    setLoras(newLoras);

    // If using Flux LoRA model, append the LoRA name to the prompt
    if (modelId && modelEndpoint) {
      // Get the model name from the API endpoint
      const isFluxLora = modelEndpoint === 'fal-ai/flux-lora';
      if (isFluxLora) {
        const loraName = selectedLora.name;
        setInputs(prev => {
          // If there's an existing prompt, remove any previous LoRA names
          const basePrompt = prev.prompt ? prev.prompt.split(',').slice(0, -1).join(',').trim() : '';
          const newPrompt = basePrompt ? `${basePrompt}, ${loraName}` : loraName;
          return {
            ...prev,
            prompt: newPrompt
          };
        });
      }
    }
  };

  const addLora = () => {
    setLoras([...loras, { path: '', scale: 1 }]);
  };

  const removeLora = (index) => {
    const newLoras = loras.filter((_, i) => i !== index);
    setLoras(newLoras);
  };

  const handleSizeChange = (e) => {
    const selectedSize = e.target.value;
    setImageSize(selectedSize);
    if (selectedSize !== 'custom') {
      setCustomWidth('');
      setCustomHeight('');
    }
  };

  const handleCustomWidthChange = (e) => {
    setCustomWidth(e.target.value);
  };
  
  const handleCustomHeightChange = (e) => {
    setCustomHeight(e.target.value);
  };

  const handleFileUploaded = (fieldName) => (url) => {
    setInputs(prev => ({
      ...prev,
      [fieldName]: url
    }));
  };

  const handleMultipleFilesChange = (fieldName) => (fileUrls) => {
    // Filter out null/empty values and only keep valid URLs
    const validUrls = fileUrls.filter(url => url && url.trim() !== '');
    setInputs(prev => ({
      ...prev,
      [fieldName]: validUrls
    }));
  };

  const handleModelSelect = (selectedModelId) => {
    setModelId(selectedModelId);
  };

  const handleCancel = async () => {
    if (!cancelUrl) return;

    try {
      const response = await fetch(cancelUrl, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to cancel inference');
      }

      setQueueStatus(null);
      setCancelUrl(null);
      setIsLoading(false);
      if (eventSource) {
        eventSource.close();
      }
    } catch (error) {
      console.error('Error canceling inference:', error);
      setError('Failed to cancel inference');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setIsContentPolicyViolation(false);
    setResult(null);
    setQueueStatus(null);
    setCancelUrl(null);

    try {
      // Prepare the inputs
      const formInputs = { ...inputs };

      // Handle LoRAs
      if (loras.length > 0) {
        const scale = 1 / loras.length;
        formInputs.loras = loras.map(lora => ({ ...lora, scale }));
      }

      // Handle voices
      if (voices.length > 0) {
        formInputs.voices = voices;
      }

      // Handle image size
      if (hasImageSize) {
        const sizeSchema = inputSchema && inputSchema.image_size;
        const isEnumSize = sizeSchema && Array.isArray(sizeSchema.enum) && sizeSchema.enum.length > 0;

        if (isEnumSize) {
          // Model expects a preset size keyword (e.g. 'landscape_4_3'), not a width/height object
          formInputs.image_size = imageSize;
        } else {
          let width, height;
          if (imageSize === 'custom') {
            width = parseInt(customWidth);
            height = parseInt(customHeight);
            if (width < 64 || width > 2048 || height < 64 || height > 2048) {
              setError('Custom size must be between 64x64 and 2048x2048 pixels');
              setIsLoading(false);
              return;
            }
          } else {
            [width, height] = imageSize.split('x').map(Number);
          }

          formInputs.image_size = {
            width,
            height
          };
        }
      }

      // First, start the inference process
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          modelId: modelId,
          inputs: formInputs,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const startError = new Error(errorData.error || 'Failed to start inference');
        startError.contentPolicyViolation = !!errorData.contentPolicyViolation;
        throw startError;
      }

      const { sessionId } = await response.json();

      // Close any existing EventSource
      if (eventSource) {
        eventSource.close();
      }

      // Create new EventSource for streaming updates. EventSource can't send
      // custom headers, so the token travels as a query param instead.
      const newEventSource = new EventSource(`${process.env.REACT_APP_API_URL}/api/inference/stream/${sessionId}?token=${encodeURIComponent(token || '')}`);
      setEventSource(newEventSource);

      newEventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'queue_update':
            setQueueStatus(data.data);
            break;
          case 'cancel_url':
            setCancelUrl(data.data);
            break;
          case 'result':
            setResult(data.data);
            setIsLoading(false);
            newEventSource.close();
            break;
          case 'error':
            setError(data.data.error);
            setIsContentPolicyViolation(!!data.data.contentPolicyViolation);
            setIsLoading(false);
            newEventSource.close();
            break;
          default:
            console.log('Unknown message type:', data.type);
        }
      };

      newEventSource.onerror = (error) => {
        console.error('EventSource error:', error);
        setError('Connection error occurred. Please try again.');
        setIsLoading(false);
        newEventSource.close();
      };
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
      setIsContentPolicyViolation(!!error.contentPolicyViolation);
      setIsLoading(false);
    }
  };

  const validateNumberInput = (field, value, schema) => {
    const errors = [];
    if (schema.required && (value === '' || value === null || value === undefined)) {
      errors.push('This field is required');
    }
    if (schema.min !== undefined && value < schema.min) {
      errors.push(`Minimum value is ${schema.min}`);
    }
    if (schema.max !== undefined && value > schema.max) {
      errors.push(`Maximum value is ${schema.max}`);
    }
    return errors;
  };

  const handleNumberChange = (field, value, schema) => {
    let numValue;
    if (value === '') {
      numValue = '';
    } else {
      // Parse the value and round to the nearest step
      const step = schema.step || 1;
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue)) {
        // Round to the nearest step
        const roundedValue = Math.round(parsedValue / step) * step;
        // Format to avoid floating point precision issues
        numValue = Number(roundedValue.toFixed(String(step).split('.')[1]?.length || 0));
      } else {
        numValue = '';
      }
    }

    const errors = validateNumberInput(field, numValue, schema);
    
    setInputErrors(prev => ({
      ...prev,
      [field]: errors
    }));

    handleInputChange(field, numValue);
  };

  const handleVoiceChange = (index, key, value) => {
    setVoices((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const addVoice = () => {
    if (!inputSchema?.voices) return;
    const max = inputSchema.voices.maxItems || 2;
    setVoices((prev) =>
      prev.length < max ? [...prev, { voice: '', turn_prefix: `Spk${prev.length + 1}:` }] : prev
    );
  };

  const removeVoice = (index) => {
    if (!inputSchema?.voices) return;
    const min = inputSchema.voices.minItems || 1;
    setVoices((prev) =>
      prev.length > min ? prev.filter((_, i) => i !== index) : prev
    );
  };

  const renderInput = (field, schema) => {
    // Skip rendering if schema is null or undefined
    if (!schema) return null;

    // Handle boolean input
    if (schema.type === 'boolean' || schema.fieldType === 'boolean') {
      // Support custom labels for boolean values
      const trueLabel = schema.trueLabel || schema.enumTitles?.[0] || 'True';
      const falseLabel = schema.falseLabel || schema.enumTitles?.[1] || 'False';
      
      return (
        <div key={field}>
          <label>
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
          </label>
          <select
            value={inputs[field] !== undefined ? String(inputs[field]) : String(schema.default || false)}
            onChange={(e) => handleInputChange(field, e.target.value === 'true')}
            required={schema.required}
            className="form-control"
          >
            <option value="true">{trueLabel}</option>
            <option value="false">{falseLabel}</option>
          </select>
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
        </div>
      );
    }

    // Handle select/enum input
    if (schema.fieldType === 'select' && schema.enum) {
      const CUSTOM_OPTION = '__custom__';
      const currentValue = inputs[field] !== undefined ? inputs[field] : (schema.default || '');
      const isCustomMode = schema.allowCustom && (
        customSelectFields[field] ||
        (currentValue !== '' && !schema.enum.includes(currentValue))
      );

      return (
        <div key={field}>
          <label>
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
          </label>
          {isCustomMode ? (
            <div className="custom-select-input" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text"
                value={currentValue}
                onChange={(e) => handleInputChange(field, e.target.value)}
                placeholder={`Enter custom ${(schema.title || field).toLowerCase()}`}
                required={schema.required}
                className="form-control"
              />
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setCustomSelectFields(prev => ({ ...prev, [field]: false }));
                  handleInputChange(field, schema.default || schema.enum[0] || '');
                }}
              >
                Choose from presets
              </button>
            </div>
          ) : (
            <select
              value={currentValue}
              onChange={(e) => {
                if (e.target.value === CUSTOM_OPTION) {
                  setCustomSelectFields(prev => ({ ...prev, [field]: true }));
                  handleInputChange(field, '');
                } else {
                  handleInputChange(field, e.target.value);
                }
              }}
              required={schema.required}
              className="form-control"
            >
              {schema.enum.map((value, index) => (
                <option key={value} value={value}>
                  {schema.enumTitles?.[index] || value}
                </option>
              ))}
              {schema.allowCustom && (
                <option value={CUSTOM_OPTION}>Custom…</option>
              )}
            </select>
          )}
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
        </div>
      );
    }

    // Handle file upload
    if (schema.fieldType === 'file') {
      return (
        <div key={field}>
          <label>
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
          </label>
          <FileUpload
            key={`${formResetKey}-${field}`}
            onFileUploaded={handleFileUploaded(field)}
            accept={getFileAcceptTypes(field, schema)}
            fieldName={field}
          />
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
        </div>
      );
    }

    // Handle multiple file upload (file array)
    if (schema.fieldType === 'file-array') {
      const minItems = schema.minItems || 1;
      const maxItems = schema.maxItems || 10;
      const currentFiles = inputs[field] || [];
      
      return (
        <div key={field}>
          <label>
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
          </label>
          <MultiFileUpload
            key={`${formResetKey}-${field}`}
            onFilesChange={handleMultipleFilesChange(field)}
            accept={getFileAcceptTypes(field, schema)}
            fieldName={field}
            minItems={minItems}
            maxItems={maxItems}
            initialFiles={currentFiles}
          />
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
        </div>
      );
    }

    // Handle textarea
    if (schema.fieldType === 'textarea') {
      return (
        <div key={field}>
          <label>
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
          </label>
          <textarea
            value={inputs[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            required={schema.required}
            rows={4}
          />
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
        </div>
      );
    }

    // Handle number input
    if (schema.fieldType === 'number' || schema.type === 'number') {
      const errors = inputErrors[field] || [];
      const hasError = errors.length > 0;
      const currentValue = inputs[field] || '';
      const step = schema.step || 1;
      const isDecimal = step < 1;
      const numValue = parseFloat(currentValue) || 0;

      return (
        <div key={field} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
            {schema.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="number-input-container">
            <input
              type="number"
              value={currentValue}
              onChange={(e) => handleNumberChange(field, e.target.value, schema)}
              onBlur={(e) => handleNumberChange(field, e.target.value, schema)}
              required={schema.required}
              min={schema.min}
              max={schema.max}
              step={step}
              className={`block w-full rounded-md shadow-sm sm:text-sm
                ${hasError 
                  ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              placeholder={schema.placeholder || `Enter ${schema.title.toLowerCase()}`}
            />
            <div className="number-input-buttons">
              <button
                type="button"
                onClick={() => handleNumberChange(field, numValue + step, schema)}
                className="number-input-button"
                disabled={schema.max !== undefined && numValue >= schema.max}
                title="Increment"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => handleNumberChange(field, numValue - step, schema)}
                className="number-input-button"
                disabled={schema.min !== undefined && numValue <= schema.min}
                title="Decrement"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
          {hasError && (
            <div className="number-input-error">
              {errors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          )}
          <div className="number-input-info">
            {(schema.min !== undefined && schema.max !== undefined) || isDecimal || schema.default !== undefined ? (
              <p>
                {schema.min !== undefined && schema.max !== undefined && (
                  <span>Range: {schema.min} to {schema.max}</span>
                )}
                {isDecimal && (
                  <span>{schema.min !== undefined && schema.max !== undefined ? ' | ' : ''}Step: {step}</span>
                )}
                {schema.default !== undefined && (
                  <span>
                    {(schema.min !== undefined || isDecimal) ? ' | ' : ''}Default: {schema.default}
                  </span>
                )}
              </p>
            ) : null}
            {schema.description && <p>{schema.description}</p>}
          </div>
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
        </div>
      );
    }

    // Handle LoRAs
    if (field === 'loras') {
      return (
        <div key={field} className="lora-container">
          <label>
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
          </label>
          {loras.map((lora, index) => (
            <div key={index} className="lora-item">
              <select
                value={availableLoras.find(l => l.path === lora.path)?.id || ''}
                onChange={(e) => handleLoraChange(index, e.target.value)}
              >
                <option value="">Select a LoRA</option>
                {availableLoras.map((availableLora) => (
                  <option key={availableLora.id} value={availableLora.id}>
                    {availableLora.name}
                  </option>
                ))}
              </select>
              <button 
                type="button" 
                onClick={() => removeLora(index)}
                className="remove-lora-button"
              >
                Remove
              </button>
            </div>          ))}
          <button 
            type="button" 
            onClick={addLora}
            className="add-lora-button"
          >
            Add LoRA
          </button>
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
        </div>
      );
    }

    // Handle voices array input
    if (field === 'voices' && schema.type === 'array' && schema.items && schema.items.properties) {
      const min = schema.minItems || 1;
      const max = schema.maxItems || 2;
      const voiceEnum = schema.items.properties.voice.enum || [];
      return (
        <div key={field} className="voices-container">
          <label>
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
          </label>
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
          {voices.map((voiceObj, idx) => (
            <div key={idx} className="voice-item" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                value={voiceObj.voice}
                onChange={e => handleVoiceChange(idx, 'voice', e.target.value)}
                required
              >
                <option value="">Select a voice</option>
                {voiceEnum.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Turn prefix"
                value={voiceObj.turn_prefix}
                onChange={e => handleVoiceChange(idx, 'turn_prefix', e.target.value)}
                required
                style={{ marginLeft: 8, minWidth: 80 }}
              />
              {voices.length > min && (
                <button type="button" className="remove-lora-button" onClick={() => removeVoice(idx)}>
                  Remove
                </button>
              )}
            </div>
          ))}
          {voices.length < max && (
            <button type="button" className="add-lora-button" onClick={addVoice}>
              Add Voice
            </button>
          )}
          <div className="voice-count-info">Speakers: {voices.length} (min {min}, max {max})</div>
        </div>
      );
    }

    // Handle image size
    if (field === 'image_size') {
      const isEnumSize = Array.isArray(schema.enum) && schema.enum.length > 0;

      if (isEnumSize) {
        return (
          <div key={field}>
            <label>{schema.title}</label>
            <div className="form-group">
              <select
                value={imageSize}
                onChange={handleSizeChange}
                className="form-control"
              >
                {schema.enum.map((value, i) => (
                  <option key={value} value={value}>
                    {(schema.enumTitles && schema.enumTitles[i]) || value}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      }

      return (
        <div key={field}>
          <label>{schema.title}</label>
          <div className="form-group">
            <select
              value={imageSize}
              onChange={handleSizeChange}
              className="form-control"
            >
              <option value="512x512">Square (512x512)</option>
              <option value="1024x1024">Square HD (1024x1024)</option>
              <option value="768x1024">Portrait 4:3 (768x1024)</option>
              <option value="576x1024">Portrait 9:16 (576x1024)</option>
              <option value="1024x768">Landscape 4:3 (1024x768)</option>
              <option value="1024x576">Landscape 9:16 (1024x576)</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {imageSize === 'custom' && (
            <div className="form-group custom-size">
              <label htmlFor="customWidth">Width (px):</label>
              <input
                type="number"
                id="customWidth"
                value={customWidth}
                onChange={handleCustomWidthChange}
                className="form-control"
                min="64"
                max="2048"
              />
              <label htmlFor="customHeight">Height (px):</label>
              <input
                type="number"
                id="customHeight"
                value={customHeight}
                onChange={handleCustomHeightChange}
                className="form-control"
                min="64"
                max="2048"
              />
            </div>
          )}
        </div>
      );
    }

    // Only render text input for fields that are explicitly marked as text or have no fieldType
    if (schema.fieldType === 'text' || !schema.fieldType) {
      return (
        <div key={field}>
          <label>
            {schema.title}
            {schema.description && (
              <span className="info-icon" title={schema.description}>?</span>
            )}
          </label>
          <input
            type="text"
            value={inputs[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            required={schema.required}
          />
          {schema.notes && (
            <div className="input-notes">{schema.notes}</div>
          )}
        </div>
      );
    }

    return null;
  };


  const renderResult = (result) => {
    if (!result || !result.data) return <p>No content generated</p>;

    // Handle video output
    if (result.data.video) {
      return (
        <div className="video-item bg-gray-100 p-4 rounded-lg">
          <div className="relative w-full overflow-hidden rounded-lg">
            <video 
              src={result.data.video.url} 
              controls
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
          <div className="mt-2 text-sm text-gray-600">
            <p>URL: <a href={result.data.video.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{result.data.video.url}</a></p>
            <p>File Size: {(result.data.video.file_size / 1024 / 1024).toFixed(2)} MB</p>
            <p>Content Type: {result.data.video.content_type}</p>
          </div>
        </div>
      );
    }

    // Handle image output
    if (result.data.images) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {result.data.images.map((image, index) => (
            <div key={index} className="image-item bg-gray-100 p-4 rounded-lg">
              <div className="relative w-full overflow-hidden rounded-lg">
                <img 
                  src={image.url} 
                  alt={`Generated ${index + 1}`}
                  style={{ width: '100%', height: 'auto' }}
                />
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <p>URL: <a href={image.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{image.url}</a></p>
                {image.width && image.height && (
                  <p>Dimensions: {image.width} x {image.height}</p>
                )}
                {image.content_type && (
                  <p>Content Type: {image.content_type}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Handle single image output
    if (result.data.image) {
      return (
        <div className="image-item bg-gray-100 p-4 rounded-lg">
          <div className="relative w-full overflow-hidden rounded-lg">
            <img 
              src={result.data.image} 
              alt="Generated"
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
          <div className="mt-2 text-sm text-gray-600">
            <p>URL: <a href={result.data.image} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{result.data.image}</a></p>
          </div>
        </div>
      );
    }

    // Handle audio output
    if (result.data.audio && result.data.audio.url) {
      return (
        <div className="audio-item bg-gray-100 p-4 rounded-lg">
          <audio controls src={result.data.audio.url} style={{ width: '100%' }} />
        </div>
      );
    }
    // Handle new audio_file output
    if (result.data.audio_file && result.data.audio_file.url) {
      return (
        <div className="audio-item bg-gray-100 p-4 rounded-lg">
          <audio controls src={result.data.audio_file.url} style={{ width: '100%' }} />
          <div className="mt-2 text-sm text-gray-600">
            <p>URL: <a href={result.data.audio_file.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{result.data.audio_file.url}</a></p>
            {result.data.audio_file.file_name && (
              <p>File Name: {result.data.audio_file.file_name}</p>
            )}
            {result.data.audio_file.file_size && (
              <p>File Size: {(result.data.audio_file.file_size / 1024).toFixed(1)} KB</p>
            )}
            {result.data.audio_file.content_type && (
              <p>Content Type: {result.data.audio_file.content_type}</p>
            )}
          </div>
        </div>
      );
    }

    return <p>No content generated</p>;
  };

  // Clean up EventSource on component unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  // Set default values for number and boolean fields when inputSchema changes
  useEffect(() => {
    if (inputSchema) {
      const newInputs = { ...inputs };
      let changed = false;
      Object.entries(inputSchema).forEach(([field, schema]) => {
        // Handle number defaults
        if (
          (schema.fieldType === 'number' || schema.type === 'number') &&
          schema.default !== undefined &&
          (newInputs[field] === undefined || newInputs[field] === '')
        ) {
          newInputs[field] = schema.default;
          changed = true;
        }
        // Handle boolean defaults
        else if (
          (schema.fieldType === 'boolean' || schema.type === 'boolean') &&
          newInputs[field] === undefined
        ) {
          newInputs[field] = schema.default !== undefined ? schema.default : false;
          changed = true;
        }
      });
      if (changed) setInputs(newInputs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSchema]);

  return (
    <div className="inference-form">
      <ModelSelector onModelSelect={handleModelSelect} />
      {selectedModel && selectedModel.status === 'test' && (
        <div style={{ 
          marginBottom: 8, 
          padding: '6px 10px', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: 4, 
          fontSize: '0.85rem',
          color: '#856404'
        }}>
          🧪 <strong>Test Model:</strong> This model is in testing phase
        </div>
      )}
      {selectedModel && selectedModel.cost && (
        <div className="model-cost-info">
          <small>{selectedModel.cost}</small>
        </div>
      )}
      {selectedModel && selectedModel.description && (
        <div className="model-description">
          {selectedModel.description}
        </div>
      )}
      <form onSubmit={handleSubmit}>        {inputSchema && Object.entries(inputSchema)
          .filter(([field, schema]) => {
            return schema && typeof schema === 'object' && schema.title && !schema.hidden;
          })
          .sort(([, a], [, b]) => (a.order || Infinity) - (b.order || Infinity))
          .map(([field, schema]) => renderInput(field, schema))
        }
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Processing...' : 'Generate'}
        </button>
      </form>
      {isLoading && (
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-2"></div>
              <span className="text-gray-600">
                {queueStatus ? `Processing: ${queueStatus.status}` : 'Starting...'}
              </span>
            </div>
            {cancelUrl && (
              <button
                onClick={handleCancel}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
      {error && isContentPolicyViolation && (
        <div className="content-policy-violation">
          <strong>⚠️ Content Flagged</strong>
          <p>{error}</p>
        </div>
      )}
      {error && !isContentPolicyViolation && <div className="error">{error}</div>}
      {result && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Generated Content</h3>
          {renderResult(result)}
          <div className="mt-4">
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetails && (
              <div className="mt-2 p-4 bg-gray-100 rounded-lg">
                <pre className="text-sm overflow-auto">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InferenceForm;
